import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, BookOpen, ShoppingBag, Users, DollarSign, TrendingUp, UserCog, Wallet, 
  Star, Megaphone, Shield, Settings, Play, Sparkles, Crown, Link2, PiggyBank, 
  Layout, Layers, Store, MessageCircle, QrCode, Trophy, Share2, CreditCard, Bell,
  ChevronDown, ChevronRight, Zap, Globe, Palette, Lock, BarChart3, Send, Ticket, Building2, Gavel, Receipt, Wrench,
  Gamepad2, Coins, FileText, HeartHandshake, Tv, Bot, Monitor, Heart, Bike, CheckCircle, XCircle
} from 'lucide-react';
import api from '../../utils/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingDeposits, setPendingDeposits] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    core: true,
    marketing: false,
    tools: true,
    settings: false
  });

  useEffect(() => {
    fetchStats();
    // Poll AT-deposit pending count every 60s
    const loadPending = () => api.get('/at-deposit/admin/requests', { params: { status: 'pending', limit: 200 } })
      .then(r => setPendingDeposits(r.data.total || 0))
      .catch(() => {});
    loadPending();
    const iv = setInterval(loadPending, 60000);
    return () => clearInterval(iv);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const Section = ({ id, title, icon: Icon, color, children }) => (
    <div className="mb-8">
      <button
        onClick={() => toggleSection(id)}
        className={`w-full flex items-center justify-between p-4 rounded-xl bg-gradient-to-r ${color} hover:opacity-90 transition-all mb-4`}
        data-testid={`section-${id}`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-6 h-6" />
          <h2 className="text-xl font-bold">{title}</h2>
        </div>
        {expandedSections[id] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
      </button>
      {expandedSections[id] && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fadeIn">
          {children}
        </div>
      )}
    </div>
  );

  const QuickLink = ({ to, icon: Icon, title, desc, color = "text-primary", border = "" }) => (
    <Link
      to={to}
      className={`glass-card p-5 hover:bg-white/5 transition-all group ${border}`}
      data-testid={`link-${to.replace('/admin/', '')}`}
    >
      <Icon className={`w-7 h-7 ${color} mb-2 group-hover:scale-110 transition-transform`} />
      <h3 className="font-semibold">{title}</h3>
      <p className="text-muted-foreground text-xs mt-1">{desc}</p>
    </Link>
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl md:text-4xl font-bold" data-testid="admin-title">Admin Dashboard</h1>
          <Link 
            to="/admin/notifications" 
            className="p-3 rounded-xl bg-primary/10 hover:bg-primary/20 transition-colors relative"
            data-testid="notifications-btn"
          >
            <Bell className="w-6 h-6 text-primary" />
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
              {stats?.pending_notifications || 0}
            </span>
          </Link>
        </div>

        {/* AI Usage & Cost Control — Prominent shortcut (per user directive Feb 2026) */}
        <PendingPartnersBanner />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <Link
            to="/admin/ai-usage"
            data-testid="ai-usage-shortcut-banner"
            className="block group"
          >
            <div className="relative overflow-hidden rounded-2xl border-2 border-red-500/60 bg-gradient-to-br from-red-500/15 via-orange-500/10 to-amber-500/15 p-4 sm:p-5 hover:scale-[1.01] transition-all h-full">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-red-500 text-white flex items-center justify-center shadow-lg">
                  <BarChart3 className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-400/20 text-red-200 border border-red-400/30">
                      🔥 AI USAGE · COST TRACKER
                    </span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-extrabold text-red-100 leading-tight">
                    View AI API costs & usage
                  </h3>
                  <p className="text-xs sm:text-sm text-red-100/70 mt-0.5">
                    Nano Banana · GPT-4o · Trial Room · Live cost breakdown
                  </p>
                </div>
              </div>
            </div>
          </Link>
          <Link
            to="/admin/ai-control"
            data-testid="ai-control-shortcut-banner"
            className="block group"
          >
            <div className="relative overflow-hidden rounded-2xl border-2 border-purple-500/60 bg-gradient-to-br from-purple-500/15 via-fuchsia-500/10 to-pink-500/15 p-4 sm:p-5 hover:scale-[1.01] transition-all h-full">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-purple-500 text-white flex items-center justify-center shadow-lg">
                  <Bot className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-400/20 text-purple-200 border border-purple-400/30">
                      ⚡ AI CONTROL PANEL
                    </span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-extrabold text-purple-100 leading-tight">
                    Manage AI switches & voice
                  </h3>
                  <p className="text-xs sm:text-sm text-purple-100/70 mt-0.5">
                    Toggle features · Rate limits · Model config
                  </p>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* 🩺 PDF Font Health Diagnostic — expandable */}
        <div className="mb-6">
          <PdfFontHealthCheck />
        </div>

        {/* AT Deposits — Pending requests banner (prominent CTA) */}
        <Link
          to="/anant-admin?tab=atdeposit"
          data-testid="atdeposit-pending-banner"
          className="block mb-6 group"
        >
          <div className={`relative overflow-hidden rounded-2xl border-2 ${pendingDeposits > 0 ? 'border-amber-400 bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-rose-500/15 animate-pulse-once' : 'border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-slate-900'} p-4 sm:p-5 hover:scale-[1.01] transition-all`}>
            <div className="flex items-center gap-4">
              <div className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${pendingDeposits > 0 ? 'bg-amber-500 text-black' : 'bg-emerald-500/30 text-emerald-300'} flex items-center justify-center shadow-lg`}>
                <Wallet className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/30">
                    BUY AT BALANCE · USER DEPOSITS
                  </span>
                </div>
                <h3 className="text-lg sm:text-2xl font-extrabold text-amber-200 leading-tight">
                  {pendingDeposits > 0
                    ? `${pendingDeposits} pending request${pendingDeposits === 1 ? '' : 's'} awaiting your approval`
                    : 'AT Deposits — No pending requests'}
                </h3>
                <p className="text-xs sm:text-sm text-amber-100/70 mt-0.5">
                  Click to review · UPI/QR/Bank screenshots · 1 INR = 1 AT credit on approval
                </p>
              </div>
              {pendingDeposits > 0 && (
                <div className="flex-shrink-0 text-center px-3 py-2 rounded-xl bg-amber-500 text-black">
                  <div className="text-2xl sm:text-3xl font-black leading-none">{pendingDeposits}</div>
                  <div className="text-[9px] sm:text-[10px] uppercase font-bold tracking-wider">Pending</div>
                </div>
              )}
            </div>
          </div>
        </Link>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="glass-card p-5" data-testid="stat-revenue">
            <DollarSign className="w-7 h-7 text-green-500 mb-2" />
            <p className="text-2xl font-bold">₹{stats?.total_revenue?.toFixed(0) || '0'}</p>
            <p className="text-muted-foreground text-sm">Revenue</p>
          </div>
          <div className="glass-card p-5" data-testid="stat-orders">
            <ShoppingBag className="w-7 h-7 text-primary mb-2" />
            <p className="text-2xl font-bold">{stats?.total_orders || 0}</p>
            <p className="text-muted-foreground text-sm">Orders</p>
          </div>
          <div className="glass-card p-5" data-testid="stat-products">
            <Package className="w-7 h-7 text-purple-500 mb-2" />
            <p className="text-2xl font-bold">{stats?.total_products || 0}</p>
            <p className="text-muted-foreground text-sm">Products</p>
          </div>
          <div className="glass-card p-5" data-testid="stat-users">
            <Users className="w-7 h-7 text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
            <p className="text-muted-foreground text-sm">Users</p>
          </div>
        </div>

        {/* Core Business */}
        <Section id="core" title="Core Business" icon={Zap} color="from-primary/20 to-purple-500/20">
          <QuickLink to="/admin/products" icon={Package} title="Products" desc="Manage inventory" color="text-primary" />
          <QuickLink to="/admin/courses" icon={BookOpen} title="Courses" desc="Educational content" color="text-purple-500" />
          <QuickLink to="/admin/orders" icon={ShoppingBag} title="Orders" desc="View all orders" color="text-green-500" />
          <QuickLink to="/admin/users" icon={UserCog} title="Users" desc="Manage members" color="text-blue-500" />
          <QuickLink to="/admin/fund-requests" icon={Wallet} title="Fund Requests" desc="Approve top-ups" color="text-orange-500" />
          <QuickLink to="/admin/activation-packs" icon={Crown} title="Activation Packs" desc="Affiliate Network packages" color="text-yellow-500" border="border border-yellow-500/30" />
          <QuickLink to="/admin/games" icon={Gamepad2} title="Game Manager" desc="Fun & Entertainment" color="text-pink-500" border="border border-pink-500/30" />
          <QuickLink to="/admin/game-wallets" icon={Coins} title="Game Wallets" desc="AGP System Wallets" color="text-yellow-500" border="border border-yellow-500/30" />
          <QuickLink to="/admin/avx" icon={TrendingUp} title="AVX Exchange" desc="Anant Virtual Trading + 7 Wallets + Affiliate Network" color="text-yellow-400" border="border-2 border-yellow-500/50" />
          <QuickLink to="/admin/tsb" icon={Coins} title="TSB Rewards" desc="Closed-Loop Loyalty + 50/50 Marketplace + Maker-Checker" color="text-amber-300" border="border-2 border-amber-500/50" />
          <QuickLink to="/admin/biodata" icon={FileText} title="Bio Data Manager" desc="Templates & Biodatas" color="text-amber-500" border="border border-amber-500/30" />
          <QuickLink to="/admin/p2p" icon={ShoppingBag} title="P2P Marketplace" desc="Listings & passes" color="text-cyan-500" border="border border-cyan-500/30" />
          <QuickLink to="/admin/auction" icon={Gavel} title="Auction House" desc="Manage auctions" color="text-red-500" border="border border-red-500/30" />
          <QuickLink to="/admin/franchise" icon={Building2} title="Franchise System" desc="City & District Captains" color="text-amber-500" border="border border-amber-500/30" />
          <QuickLink to="/admin/passes" icon={Ticket} title="Pass Management" desc="Coupons & passes" color="text-purple-500" border="border border-purple-500/30" />
          <QuickLink to="/admin/ledger" icon={Receipt} title="Transaction Ledger" desc="Financial overview" color="text-teal-500" border="border border-teal-500/30" />
          <QuickLink to="/admin/payment-settings" icon={CreditCard} title="Payments" desc="Gateway settings" color="text-emerald-500" border="border border-emerald-500/30" />
          <QuickLink to="/admin/system-wallets" icon={PiggyBank} title="System Wallets" desc="Maintenance & Promo" color="text-orange-500" />
        </Section>

        {/* Marketing & Engagement */}
        <Section id="marketing" title="Marketing & Engagement" icon={Megaphone} color="from-pink-500/20 to-orange-500/20">
          <QuickLink to="/admin/broadcasts" icon={Megaphone} title="Broadcasts" desc="Send announcements" color="text-orange-500" />
          <QuickLink to="/admin/testimonials" icon={Star} title="Testimonials" desc="Manage reviews" color="text-yellow-500" />
          <QuickLink to="/admin/popups" icon={Sparkles} title="Popups" desc="Promotional popups" color="text-pink-500" />
          <QuickLink to="/admin/hot-news" icon={Zap} title="Hot News" desc="Breaking news ticker" color="text-red-500" />
          <QuickLink to="/admin/ranks-rewards" icon={Trophy} title="Ranks & Rewards" desc="Gamification" color="text-yellow-500" border="border border-yellow-500/30" />
          <QuickLink to="/admin/affiliate" icon={Share2} title="Affiliate" desc="Commission system" color="text-pink-500" border="border border-pink-500/30" />
          <QuickLink to="/admin/push-notifications" icon={Bell} title="Push Notifications" desc="PWA notifications" color="text-blue-500" border="border border-blue-500/30" />
        </Section>

        {/* Tools & Builders */}
        <Section id="tools" title="Tools & Builders" icon={Globe} color="from-cyan-500/20 to-emerald-500/20">
          <QuickLink to="/admin/system-auditor" icon={Sparkles} title="🛰️ System Auditor" desc="LIVE feed · All requests · Voice commands · Auto-Read Hindi TTS" color="text-red-400" border="border-2 border-red-500/70" />
          <QuickLink to="/admin/ai-control" icon={Bot} title="AI Control Panel" desc="Manage AI & Voice costs" color="text-red-500" border="border-2 border-red-500/50" />
          <QuickLink to="/admin/support" icon={MessageCircle} title="Support Helpdesk" desc="FREE user support" color="text-green-500" border="border-2 border-green-500/50" />
          <QuickLink to="/admin/marketing" icon={TrendingUp} title="Marketing AI" desc="AstraPulse Admin" color="text-purple-500" border="border border-purple-500/30" />
          <QuickLink to="/admin/sankat-mochan" icon={HeartHandshake} title="Sankat Mochan" desc="Samadhan Admin" color="text-green-500" border="border border-green-500/30" />
          <QuickLink to="/admin/sankat-vip" icon={Sparkles} title="Sankat VIP Panel" desc="VIP session duration · saved sessions · Knowledge Base (RAG)" color="text-amber-500" border="border-2 border-amber-500/60" />
          <QuickLink to="/admin/anant-feelings" icon={Heart} title="Anant Feelings Admin" desc="Personas · pricing · prompts · Knowledge Base" color="text-rose-400" border="border-2 border-rose-500/60" />
          <QuickLink to="/admin/academy-quizzes" icon={Trophy} title="Academy Quizzes" desc="Course quizzes · cert unlock · smart school" color="text-amber-400" border="border-2 border-amber-500/60" />
          <QuickLink to="/admin/display-windows" icon={Tv} title="Live Display" desc="Homepage Videos" color="text-red-500" border="border border-red-500/30" />
          <QuickLink to="/admin/displayengine" icon={Monitor} title="Display Engine" desc="Signage boards · 6 templates · Auto-translate" color="text-amber-500" border="border-2 border-amber-500/60" />
          <QuickLink to="/admin/algotrading" icon={TrendingUp} title="Anant Smart Trading Admin" desc="Bots · AI Signals · Brokers · Affiliate Network · Market Data" color="text-violet-500" border="border-2 border-violet-500/60" />
          <QuickLink to="/admin/vyapar" icon={Store} title="Anant Smart Vyapar" desc="CRM subs · DFY leads · Network defaulters · Export block" color="text-fuchsia-500" border="border-2 border-fuchsia-500/60" />
          <QuickLink to="/admin/safety-qr" icon={QrCode} title="Anant Safety QR Admin" desc="QR stock · scans · spam block · subscriptions" color="text-red-500" border="border-2 border-red-500/60" />
          <QuickLink to="/admin/apts" icon={QrCode} title="APTS Portfolio Admin" desc="Subscriptions · grants · lock/unlock · cron · revenue" color="text-indigo-500" border="border-2 border-indigo-500/60" />
          <QuickLink to="/admin/asst" icon={QrCode} title="ASST Safety Admin" desc="System safety subs · scans · threats · Affiliate Network revenue" color="text-cyan-500" border="border-2 border-cyan-500/60" />
          <QuickLink to="/admin/samadhan" icon={Sparkles} title="Samadhan Center Admin" desc="🏛️ Dream Project · Founders Wall · Reservoir · Gallery · Test Seed" color="text-amber-400" border="border-2 border-amber-400/70" />
          <QuickLink to="/admin/trial-room" icon={Sparkles} title="AI Trial Room Admin" desc="🪞 Products Manager · Subscriptions · Retailer Inventory · Revenue" color="text-cyan-400" border="border-2 border-cyan-400/60" />
          <QuickLink to="/admin/new-tools" icon={Sparkles} title="🛠️ New Tools Admin (BrandMark · LeadGen · Corpus50 · Vault)" desc="Unified admin for 4 new tools · Subs · Leads · Payments · Vault passphrase reset · WA reminders" color="text-fuchsia-400" border="border-2 border-fuchsia-400/70" />
          <QuickLink to="/admin/free-tools" icon={Wrench} title="Free Tools" desc="Manage tools" color="text-cyan-500" border="border border-cyan-500/30" />
          <QuickLink to="/admin/ai-tools" icon={Sparkles} title="AI Creator Tools" desc="Tours & Video Ads" color="text-purple-500" border="border border-purple-500/30" />
          <QuickLink to="/admin/tools-analytics" icon={BarChart3} title="Tools Analytics" desc="Usage tracking" color="text-blue-500" border="border border-blue-500/30" />
          <QuickLink to="/admin/share-analytics" icon={Share2} title="Share Analytics" desc="Social share tracking" color="text-green-500" border="border border-green-500/30" />
          <QuickLink to="/admin/supertool" icon={Wrench} title="SuperTool" desc="Account Book + Calendar" color="text-purple-500" border="border border-purple-500/30" />
          <QuickLink to="/admin/qr-leads" icon={QrCode} title="QR Lead Collector" desc="WhatsApp lead tool" color="text-green-500" border="border border-green-500/30" />
          <QuickLink to="/admin/shop-builder" icon={Store} title="Shop Builder" desc="Create websites" color="text-emerald-500" border="border border-emerald-500/30" />
          <QuickLink to="/admin/page-builder" icon={Layout} title="Page Builder" desc="Landing pages" color="text-cyan-500" border="border border-cyan-500/30" />
          <QuickLink to="/admin/voice-agent" icon={MessageCircle} title="Voice Agent" desc="AI Assistant (Priya)" color="text-pink-500" border="border border-pink-500/30" />
          <QuickLink to="/admin/voice-control" icon={Megaphone} title="Voice Control" desc="Hindi announcements" color="text-orange-500" border="border border-orange-500/30" />
          <QuickLink to="/admin/whatsapp-leads" icon={MessageCircle} title="WhatsApp Leads" desc="Lead capture system" color="text-green-500" border="border border-green-500/30" />
          <QuickLink to="/admin/whatsapp-bot" icon={Bot} title="WhatsApp Bot" desc="Telegram notifications" color="text-green-500" border="border border-green-500/30" />
          <QuickLink to="/admin/magic-qr" icon={QrCode} title="Magic QR Tool" desc="Review collector" color="text-amber-500" border="border border-amber-500/30" />
          <QuickLink to="/admin/custom-tabs" icon={Layers} title="Custom Tabs" desc="Dashboard tabs" color="text-purple-500" />
        </Section>

        {/* Settings & Security */}
        <Section id="settings" title="Settings & Security" icon={Settings} color="from-gray-500/20 to-slate-500/20">
          <QuickLink to="/admin/settings" icon={Settings} title="General Settings" desc="Site configuration" color="text-gray-400" />
          <QuickLink to="/admin/2fa" icon={Shield} title="2FA Security" desc="Two-factor auth" color="text-green-500" />
          <QuickLink to="/admin/important-links" icon={Link2} title="Important Links" desc="Footer & legal" color="text-blue-500" />
        </Section>

        {/* Recent Orders */}
        {stats?.recent_orders && stats.recent_orders.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold mb-4">Recent Orders</h2>
            <div className="space-y-3">
              {stats.recent_orders.slice(0, 5).map((order) => (
                <div
                  key={order.id}
                  className="glass-card p-4 flex justify-between items-center"
                  data-testid={`recent-order-${order.id}`}
                >
                  <div>
                    <p className="font-semibold">Order #{order.id.slice(0, 8)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString()} • {order.items?.length || 0} items
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">₹{order.total_amount?.toFixed(2) || '0.00'}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      order.payment_status === 'completed' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'
                    }`}>
                      {order.payment_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;

// ============ Pending Delivery Partners Banner ============
function PendingPartnersBanner() {
  const [pending, setPending] = useState([]);
  const [counts, setCounts] = useState({});
  const [busyIds, setBusyIds] = useState({});
  const [expanded, setExpanded] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get('/admin/delivery/partners?status=pending');
      setPending(r.data.partners || []); setCounts(r.data.counts || {});
    } catch {}
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const act = async (id, action, name) => {
    if (action === 'reject' && !window.confirm(`Reject "${name}"?`)) return;
    setBusyIds((p) => ({ ...p, [id]: true }));
    try {
      await api.post(`/admin/delivery/partner/${id}/action?action=${action}`, {});
      load();
    } catch (e) { alert(e?.response?.data?.detail || `${action} failed`); }
    finally { setBusyIds((p) => ({ ...p, [id]: false })); }
  };

  if (pending.length === 0 && (counts.pending || 0) === 0) return null;

  return (
    <div className="mb-4 rounded-2xl border-2 border-amber-400/60 bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-rose-500/10 overflow-hidden" data-testid="dashboard-pending-partners-banner">
      <button onClick={() => setExpanded((v) => !v)} className="w-full p-4 flex items-center gap-3 text-left" data-testid="pending-partners-toggle">
        <div className="w-12 h-12 rounded-xl bg-amber-500 text-slate-900 flex items-center justify-center shadow-lg animate-pulse">
          <Bike className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/30 text-amber-100 border border-amber-400/50">
              ⏳ ACTION NEEDED
            </span>
          </div>
          <h3 className="text-lg font-extrabold text-amber-100">
            {pending.length} delivery partner application{pending.length !== 1 ? 's' : ''} pending approval
          </h3>
          <p className="text-xs text-amber-100/80 mt-0.5">
            Click to review · Or go to <Link to="/admin/delivery-partners" className="underline font-black text-amber-200" onClick={(e) => e.stopPropagation()}>full partners page</Link>
          </p>
        </div>
        <ChevronDown className={`w-5 h-5 text-amber-200 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-amber-400/30 divide-y divide-amber-400/20" data-testid="pending-partners-inline-list">
          {pending.map((p) => (
            <div key={p.id} className="p-3 flex items-center gap-3 flex-wrap hover:bg-white/5" data-testid={`inline-partner-${p.id.slice(0,8)}`}>
              <div className="w-10 h-10 rounded-full bg-amber-500 text-slate-900 flex items-center justify-center text-base font-black">
                {p.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-[160px]">
                <div className="text-sm font-black">{p.name}</div>
                <div className="text-[11px] opacity-80">📞 {p.phone} · 📍 {p.city}{p.zone ? ` · ${p.zone}` : ''} · 🛵 {p.vehicle_type}{p.vehicle_number ? ` · ${p.vehicle_number}` : ''}</div>
                {p.notes && <div className="text-[10px] opacity-70 italic mt-0.5">"{p.notes}"</div>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => act(p.id, 'approve', p.name)} disabled={busyIds[p.id]}
                        data-testid={`inline-partner-${p.id.slice(0,8)}-approve`}
                        className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-xs font-black flex items-center gap-1 disabled:opacity-40">
                  <CheckCircle size={14} /> Approve
                </button>
                <button onClick={() => act(p.id, 'reject', p.name)} disabled={busyIds[p.id]}
                        data-testid={`inline-partner-${p.id.slice(0,8)}-reject`}
                        className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-black flex items-center gap-1 disabled:opacity-40">
                  <XCircle size={14} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 🩺 PDF Font Health Check — one-click diagnostic for production PDF issues
// ─────────────────────────────────────────────────────────────
function PdfFontHealthCheck() {
  const [data, setData] = React.useState(null);
  const [sys, setSys] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const runCheck = async () => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([
        api.get('/sankat-mochan/health/pdf-fonts'),
        api.get('/sankat-mochan/health/system-status').catch((e) => ({ data: { error: e.message } })),
      ]);
      setData(f.data);
      setSys(s.data);
      setExpanded(true);
    } catch (e) {
      setData({ error: e.response?.data?.detail || e.message });
      setExpanded(true);
    }
    setLoading(false);
  };

  const allOk = data?.summary?.all_ok;
  const passing = data?.summary?.passing || 0;
  const total = data?.summary?.total_langs || 0;

  return (
    <div className="rounded-2xl border-2 border-teal-400/50 bg-gradient-to-br from-teal-950/40 to-slate-900 p-4" data-testid="pdf-font-health-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-teal-300 font-black">🩺 PDF FONT DIAGNOSTIC</div>
          <h3 className="text-lg font-black">PDF Health Check</h3>
          <p className="text-xs text-slate-400">One-click check for production PDF font issues (Sankat Mochan reports).</p>
        </div>
        <div className="flex items-center gap-2">
          {data && !data.error && (
            <div className={`px-3 py-1.5 rounded-full text-xs font-black border ${allOk ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/50' : 'bg-rose-500/20 text-rose-200 border-rose-400/50'}`}>
              {allOk ? `✅ ${passing}/${total} langs OK` : `⚠️ ${passing}/${total} langs OK`}
            </div>
          )}
          <button
            onClick={runCheck}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg bg-teal-500 text-white text-xs font-black disabled:opacity-50"
            data-testid="pdf-font-health-run"
          >
            {loading ? 'Checking…' : (data ? 'Re-run' : 'Run Check')}
          </button>
        </div>
      </div>

      {expanded && data && (
        <div className="mt-3 space-y-3">
          {/* 🖥 System Status — MongoDB / uploads / LLM key / disk */}
          {sys && !sys.error && sys.checks && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-1">System Status</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {Object.entries(sys.checks).filter(([k]) => !['recent_errors','top_collections'].includes(k)).map(([k, v]) => (
                  <div key={k} className={`rounded-lg border p-2 text-[11px] ${v.ok ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-rose-500/10 border-rose-500/40'}`} data-testid={`sys-check-${k}`}>
                    <div className="font-black text-white capitalize">{k.replace(/_/g, ' ')}</div>
                    <div className={`text-[10px] font-black mt-0.5 ${v.ok ? 'text-emerald-300' : 'text-rose-300'}`}>
                      {v.ok ? '✓ OK' : '✗ FAIL'}
                    </div>
                    <div className="text-[9px] text-slate-400 mt-0.5">
                      {k === 'uploads_folder' && v.ok && `${v.size_mb} MB · ${v.file_count} files`}
                      {k === 'disk' && v.ok && `${v.free_gb} GB free · ${v.pct_used}% used`}
                      {k === 'emergent_llm_key' && (v.length ? `len=${v.length}` : 'MISSING')}
                      {k === 'mongodb' && (v.detail || '').slice(0, 40)}
                      {!v.ok && v.detail && v.detail.slice(0, 60)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Recent errors */}
              {sys.checks.recent_errors && (
                <div className="mt-2" data-testid="sys-check-recent_errors">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-1">
                    Recent Errors {sys.checks.recent_errors.ok ? '· ✅ Clean' : `· ⚠️ ${sys.checks.recent_errors.count}`}
                  </div>
                  {sys.checks.recent_errors.errors?.length > 0 ? (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {sys.checks.recent_errors.errors.map((err, i) => (
                        <div key={i} className="text-[10px] text-rose-200 bg-rose-500/10 border border-rose-500/30 rounded p-1.5 font-mono break-all">{err}</div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[10px] text-emerald-300 italic">No errors in last 200 log lines. 🎉</div>
                  )}
                </div>
              )}

              {/* Top 5 collections */}
              {sys.checks.top_collections?.top5 && (
                <div className="mt-2" data-testid="sys-check-top_collections">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-1">
                    Top 5 Collections (by doc count) · Total: {sys.checks.top_collections.total_collections || 0}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-1.5">
                    {sys.checks.top_collections.top5.map((c, i) => (
                      <div key={c.name} className="rounded-lg border border-amber-400/30 bg-amber-500/10 p-1.5 text-[10px]" data-testid={`sys-collection-${i}`}>
                        <div className="font-black text-amber-200 truncate" title={c.name}>#{i + 1} {c.name}</div>
                        <div className="text-slate-300 font-mono">{(c.docs || 0).toLocaleString()} docs</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {data.error ? (
            <div className="text-rose-300 text-sm bg-rose-500/10 border border-rose-500/40 rounded-lg p-3">
              ❌ {data.error}
            </div>
          ) : (
            <>
              {/* Fonts table */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-1">Font Files (bundled + system)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {Object.entries(data.fonts || {}).map(([name, info]) => (
                    <div key={name} className={`flex items-center justify-between gap-2 rounded-lg border p-2 text-xs ${info.registered ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-rose-500/10 border-rose-500/40'}`} data-testid={`pdf-font-row-${name}`}>
                      <div className="min-w-0">
                        <div className="font-black text-white">{name}</div>
                        <div className="text-[9px] text-slate-400 truncate">{info.path || '<missing>'}</div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {info.bundled && <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-200 font-bold">bundled</span>}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${info.registered ? 'bg-emerald-500 text-black' : 'bg-rose-500 text-white'}`}>
                          {info.registered ? '✓ REG' : '✗ MISS'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages table */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-1">Live PDF Gen Test — {total} Languages</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {Object.entries(data.languages || {}).map(([lang, info]) => (
                    <div key={lang} className={`rounded-lg border p-1.5 text-[11px] ${info.gen_ok ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-rose-500/10 border-rose-500/40'}`} data-testid={`pdf-lang-${lang}`}>
                      <div className="font-black text-white">{info.native} <span className="text-slate-400 font-normal">({lang})</span></div>
                      <div className="text-[9px] text-slate-400 truncate">{info.pref_font} → <span className="text-cyan-300">{info.safe_font}</span></div>
                      <div className={`text-[9px] font-black mt-0.5 ${info.gen_ok ? 'text-emerald-300' : 'text-rose-300'}`}>
                        {info.gen_ok ? `✓ ${(info.size_bytes / 1024).toFixed(0)} KB` : `✗ ${info.error || 'failed'}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
